import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import { setOnUnauthorized, TOKEN_KEY } from '../lib/auth';
import { queryClient } from '../queries/queryClient';

/** Where the signed-in user is cached so a reload restores the session offline. */
export const AUTH_USER_STORAGE_KEY = 'pnid_auth_user';

const DEPLOY_ENV =
  (import.meta.env.VITE_PUBLIC_DEPLOY_ENV as string | undefined) ??
  (import.meta.env.MODE as string | undefined) ??
  'unknown';

export interface AuthUser {
  username: string;
  user_id: string;
  user_group?: string;
  role?: string;
  is_reviewer?: boolean;
  permissions?: {
    can_edit?: boolean;
    can_review?: boolean;
    can_admin?: boolean;
    can_manage_packages?: boolean;
  };
}

interface AuthContextType {
  token: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  /** True while a stored token is being confirmed against `/auth/me`. */
  isVerifying: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, userGroup: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

/**
 * POST one auth request, reporting an unreachable API as itself.
 *
 * `fetch` REJECTS when nothing answers — no status, no body — and the old code
 * let that reject escape as a bare TypeError. Worse, a dev proxy with no
 * backend behind it answers 500 with a non-JSON body, so `detail || 'Login
 * failed'` printed the credential-rejection wording for an API that was simply
 * down. The two look identical on screen and cost real time to tell apart.
 */
async function postAuth(url: string, body: unknown): Promise<Response> {
  try {
    return await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error('Cannot reach the server. Check that the API is running, then try again.');
  }
}

/** Read one failed auth response into a message that says what went wrong. */
async function authError(res: Response, rejected: string): Promise<Error> {
  const detail = await res
    .json()
    .then((body) => body?.detail)
    .catch(() => undefined);
  // The API names its own reasons ("Incorrect username or password"); prefer them.
  if (typeof detail === 'string' && detail) return new Error(detail);
  if (res.status === 401 || res.status === 403) return new Error(rejected);
  if (res.status >= 500) {
    return new Error(`The server could not be reached (HTTP ${res.status}). It may be down or still starting.`);
  }
  return new Error(`${rejected} (HTTP ${res.status})`);
}

/**
 * Read one auth response into the shape the app checks permissions against.
 *
 * The backend now sends an explicit `permissions` object, but tokens minted
 * before that field existed carry only `role` and a `-reviewer` suffix on the
 * user group, so a user holding one keeps working until it expires.
 */
export function normalizeAuthUser(data: any): AuthUser {
  const permissions = data.permissions ?? {};
  const legacyReviewer =
    data.is_reviewer ??
    (data.role === 'reviewer' || (data.user_group ?? '').endsWith('-reviewer'));
  return {
    username: data.username,
    user_id: data.user_id || data.username,
    user_group: data.user_group ?? '',
    role: data.role ?? 'editor',
    is_reviewer: legacyReviewer,
    permissions: {
      can_edit: permissions.can_edit ?? !legacyReviewer,
      can_review: permissions.can_review ?? legacyReviewer,
      can_admin: permissions.can_admin ?? data.role === 'admin',
      can_manage_packages: permissions.can_manage_packages ?? false,
    },
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const posthog = usePostHog();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const s = localStorage.getItem(AUTH_USER_STORAGE_KEY);
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });
  // Block paint only when a token exists with no cached user to render from.
  const [isLoading, setIsLoading] = useState(() => Boolean(token) && !user);
  const [isVerifying, setIsVerifying] = useState(() => Boolean(token));

  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/pnid_tool';

  const persistAuth = useCallback((t: string, u: AuthUser) => {
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(u));
    setToken(t);
    setUser(u);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await postAuth(`${baseUrl}/auth/login`, { username, password });
    if (!res.ok) {
      throw await authError(res, 'Incorrect username or password.');
    }
    const data = await res.json();
    const authUser = normalizeAuthUser(data);
    persistAuth(data.access_token, authUser);
    setIsVerifying(false);
    posthog?.identify(authUser.user_id, { username: authUser.username, deploy_env: DEPLOY_ENV });
    posthog?.capture('user_logged_in', { username: authUser.username });
  }, [baseUrl, persistAuth, posthog]);

  const register = useCallback(async (username: string, userGroup: string, password: string) => {
    const res = await postAuth(`${baseUrl}/auth/register`, {
      username,
      user_group: userGroup,
      password,
    });
    if (!res.ok) {
      throw await authError(res, 'Registration failed.');
    }
    const data = await res.json();
    const authUser = normalizeAuthUser(data);
    persistAuth(data.access_token, authUser);
    setIsVerifying(false);
    posthog?.identify(authUser.user_id, { username: authUser.username, deploy_env: DEPLOY_ENV });
    posthog?.capture('user_registered', { username: authUser.username });
  }, [baseUrl, persistAuth, posthog]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    setToken(null);
    setUser(null);
    setIsLoading(false);
    setIsVerifying(false);
    // Documents/annotations/pnid-state keys are user-agnostic; leave them and
    // the next account paints the previous group's drawings from cache.
    queryClient.clear();
  }, []);

  useEffect(() => {
    setOnUnauthorized(logout);
    return () => setOnUnauthorized(() => {});
  }, [logout]);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      setIsVerifying(false);
      return;
    }
    setIsVerifying(true);
    fetch(`${baseUrl}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        logout();
      })
      .then((data) => {
        if (data) {
          const u = normalizeAuthUser(data);
          setUser((prev) => {
            if (prev && prev.user_id !== u.user_id) {
              queryClient.clear();
            }
            return u;
          });
          localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(u));
          posthog?.identify(u.user_id, { username: u.username, deploy_env: DEPLOY_ENV });
        }
      })
      .catch(() => logout())
      .finally(() => {
        setIsLoading(false);
        setIsVerifying(false);
      });
  }, [token, baseUrl, logout, posthog]);

  const value: AuthContextType = {
    token,
    user,
    isLoading,
    isVerifying,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
